/**
 * Alphabetical grouping for the kiosk ordering flow, ported from the old
 * drink-counter ordering screen.
 *
 * A fixed A–Z index is a poor fit for a club roster: half the letters hold
 * nobody and "S" holds a dozen people. Instead we start with one group per
 * letter that actually has someone behind it and repeatedly merge the adjacent
 * pair holding the fewest people, until only a handful of buttons remain. The
 * result is a small set of evenly-weighted taps rather than a sparse index.
 */

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

/** Names that do not start with a letter share one bucket, sorted first. */
const OTHER = '#'

const LETTER_ORDER = [OTHER, ...ALPHABET]

/** How many group buttons to aim for on the first ordering step. */
export const TARGET_GROUPS = 6

export interface LetterGroup {
  /**
   * Button text — every letter the group actually covers, e.g. `B-C-F-G`.
   * A range would be misleading: the empty letters in between have nobody
   * behind them, and a member looking for "D" needs to see it is not there.
   */
  label: string
  /** Every bucket letter this group covers. */
  letters: string[]
}

/** The bucket a name belongs to: its first letter, or `#`. */
export function letterBucket(name: string): string {
  const first = name.trim().charAt(0).toLocaleUpperCase()
  return /^[A-Z]$/.test(first) ? first : OTHER
}

/**
 * Splits `names` into at most `target` alphabetical groups. Returns an empty
 * array when grouping would not help — one group is just the whole list.
 */
export function letterGroups(names: string[], target = TARGET_GROUPS): LetterGroup[] {
  const counts = new Map<string, number>()
  for (const name of names) {
    const letter = letterBucket(name)
    counts.set(letter, (counts.get(letter) ?? 0) + 1)
  }

  let groups = LETTER_ORDER.filter((letter) => counts.has(letter)).map((letter) => ({
    letters: [letter],
    count: counts.get(letter) ?? 0,
  }))
  if (groups.length < 2) return []

  while (groups.length > target) {
    // Merge whichever neighbouring pair leaves the flattest distribution.
    let bestIndex = 0
    let bestCount = Infinity
    for (let i = 0; i < groups.length - 1; i++) {
      const combined = groups[i].count + groups[i + 1].count
      if (combined < bestCount) {
        bestCount = combined
        bestIndex = i
      }
    }
    groups = [
      ...groups.slice(0, bestIndex),
      {
        letters: [...groups[bestIndex].letters, ...groups[bestIndex + 1].letters],
        count: bestCount,
      },
      ...groups.slice(bestIndex + 2),
    ]
  }

  return groups.map(({ letters }) => ({ letters, label: letters.join('-') }))
}
